import React from "react";
import { Row, Col, Card, CardBody, Badge } from "reactstrap";
import { useTranslation } from "react-i18next";

const ExecutiveKpiCards = ({ financials = {}, clinical = {}, patients = {} }) => {
  const { t } = useTranslation();

  const billed = financials.total_billed || 0;
  const collected = financials.total_collected || 0;
  const dues = financials.pending_dues || 0;
  const collectionRate = financials.collection_rate || 0;
  const growth = financials.revenue_growth || 0;

  const totalAppts = clinical.total_appointments || 0;
  const newPatients = patients.new_patients || 0;
  const returningPatients = patients.returning_patients || 0;

  return (
    <Row className="g-3 mb-4">
      {/* 1. Gross Revenue Card */}
      <Col xl="3" md="6">
        <Card className="border-0 shadow-sm overflow-hidden h-100 position-relative bg-white">
          <div
            className="position-absolute top-0 start-0 end-0"
            style={{ height: "3px", background: "linear-gradient(90deg, #556ee6, #7386d5)" }}
          />
          <CardBody className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted text-uppercase font-size-11 fw-semibold tracking-wider">
                {t("Total Revenue Billed")}
              </span>
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle bg-soft-primary text-primary font-size-15">
                  <i className="bx bx-rupee" />
                </span>
              </div>
            </div>

            <h3 className="mb-2 fw-bold text-dark font-size-22">
              ₹{Number(billed).toLocaleString("en-IN")}
            </h3>

            <div className="d-flex align-items-center gap-1 font-size-12">
              <Badge color={growth >= 0 ? "soft-success" : "soft-danger"} className="px-1 py-0">
                <i className={`bx ${growth >= 0 ? "bx-trending-up" : "bx-trending-down"} me-1`} />
                {growth >= 0 ? `+${growth}%` : `${growth}%`}
              </Badge>
              <span className="text-muted font-size-11">{t("vs previous period")}</span>
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* 2. Realized Net Collections Card */}
      <Col xl="3" md="6">
        <Card className="border-0 shadow-sm overflow-hidden h-100 position-relative bg-white">
          <div
            className="position-absolute top-0 start-0 end-0"
            style={{ height: "3px", background: "linear-gradient(90deg, #34c38f, #2ecc71)" }}
          />
          <CardBody className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted text-uppercase font-size-11 fw-semibold tracking-wider">
                {t("Net Collections")}
              </span>
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle bg-soft-success text-success font-size-15">
                  <i className="bx bx-check-double" />
                </span>
              </div>
            </div>

            <h3 className="mb-2 fw-bold text-success font-size-22">
              ₹{Number(collected).toLocaleString("en-IN")}
            </h3>

            <div className="d-flex align-items-center gap-1 font-size-12">
              <span className="badge bg-soft-success text-success font-size-11 px-1 py-0 fw-semibold">
                {collectionRate}%
              </span>
              <span className="text-muted font-size-11">{t("realization efficiency")}</span>
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* 3. Outstanding / Pending Dues Card */}
      <Col xl="3" md="6">
        <Card className="border-0 shadow-sm overflow-hidden h-100 position-relative bg-white">
          <div
            className="position-absolute top-0 start-0 end-0"
            style={{ height: "3px", background: "linear-gradient(90deg, #f46a6a, #e74c3c)" }}
          />
          <CardBody className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted text-uppercase font-size-11 fw-semibold tracking-wider">
                {t("Pending Patient Dues")}
              </span>
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle bg-soft-danger text-danger font-size-15">
                  <i className="bx bx-error-circle" />
                </span>
              </div>
            </div>

            <h3 className="mb-2 fw-bold text-danger font-size-22">
              ₹{Number(dues).toLocaleString("en-IN")}
            </h3>

            <div className="d-flex align-items-center gap-1 font-size-12">
              <span className="badge bg-soft-danger text-danger font-size-11 px-1 py-0">
                {dues > 0 ? t("Action required") : t("Fully settled")}
              </span>
              <span className="text-muted font-size-11">{t("unsettled invoices")}</span>
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* 4. Total OPD Patient Footfall Card */}
      <Col xl="3" md="6">
        <Card className="border-0 shadow-sm overflow-hidden h-100 position-relative bg-white">
          <div
            className="position-absolute top-0 start-0 end-0"
            style={{ height: "3px", background: "linear-gradient(90deg, #50a5f1, #3498db)" }}
          />
          <CardBody className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted text-uppercase font-size-11 fw-semibold tracking-wider">
                {t("OPD Patient Footfall")}
              </span>
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle bg-soft-info text-info font-size-15">
                  <i className="bx bx-group" />
                </span>
              </div>
            </div>

            <h3 className="mb-2 fw-bold text-dark font-size-22">
              {totalAppts} <span className="font-size-14 text-muted fw-normal">{t("Visits")}</span>
            </h3>

            <div className="d-flex align-items-center gap-2 font-size-11 text-muted">
              <span className="badge bg-soft-primary text-primary px-1 py-0">
                👤 {newPatients} {t("New")}
              </span>
              <span className="badge bg-soft-secondary text-secondary px-1 py-0">
                🔄 {returningPatients} {t("Returning")}
              </span>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default ExecutiveKpiCards;
